import { AlertCircle, ArrowDownCircle, ArrowUpCircle, BatteryCharging, BookOpen, CheckCircle, Coins, Crown, Gift, IndianRupee, ShoppingCart, Star, Wallet, Smartphone, Share2 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import api, { API_ENDPOINTS } from '../../config/api';
import { useAuthStore } from '../../store/useAuthStore';
import LoginPrompt from '../UI/LoginPrompt';
import { getRechargeHistory } from '../../services/rechargeService';
import StoryCardModal from './StoryCardModal';

// Rows that get a "Share" button (story image for WhatsApp Status / Instagram Story):
// income, plus "payout received" which gets the withdrawal image
const STORY_INCOME_TYPES = ['commission', 'bonus', 'leadership', 'royalty', 'reward', 'fund_credit', 'shop_cashback', 'special_income_credit', 'payout_received'];
const SITE_URL = 'https://www.yesitryme.com';
const isRealId = (id) => /^[a-f0-9]{24}$/i.test(String(id || ''));
const canShareTransaction = (t) =>
    isRealId(t._id) &&
    STORY_INCOME_TYPES.includes(t.type) &&
    (t.status || 'completed') === 'completed' &&
    Number(t.amount) >= 1 &&
    !/^unassigned/i.test(t.description || '');
const formatShareAmount = (n) => Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });

const ShareButton = ({ onClick }) => (
    <button
        type="button"
        onClick={onClick}
        className="mt-2 inline-flex items-center gap-1 rounded-full bg-[#FF4E00] px-3 py-1 text-xs font-semibold text-white shadow-sm hover:bg-[#E04400] active:scale-95 transition"
        title="Create an image for your WhatsApp Status / Instagram Story"
    >
        <Share2 size={14} /> Share
    </button>
);

/** Same as rest of app: 100 coins = ₹1 */
const COINS_PER_RUPEE = 100;
const coinsToRupeesValue = (coins) => (Math.abs(Number(coins)) || 0) / COINS_PER_RUPEE;
const formatRupeesFromCoins = (coins) =>
    coinsToRupeesValue(coins).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// ---- safe formatting (bad or missing values never crash the page) ----
const toNumber = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
};
// 1250000 -> "₹12,50,000", -200 -> "−₹200", 100.5 -> "₹100.5"
const inr = (v) => {
    const n = toNumber(v);
    return `${n < 0 ? '−' : ''}₹${Math.abs(n).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
};
const statusLabel = (s) => {
    const text = String(s || 'completed');
    return text.charAt(0).toUpperCase() + text.slice(1);
};
// "••••1234" so full account numbers don't sit on screen
const maskAccount = (acc) => {
    const s = String(acc || '').replace(/\s+/g, '');
    return s.length > 4 ? `••••${s.slice(-4)}` : s;
};
const ITEMS_PER_PAGE = 10;
// Clamps a page number into 1..totalPages and returns that page's slice
const paginate = (rows, page) => {
    const totalPages = Math.max(1, Math.ceil(rows.length / ITEMS_PER_PAGE));
    const safePage = Math.min(Math.max(1, Number(page) || 1), totalPages);
    const start = (safePage - 1) * ITEMS_PER_PAGE;
    return { rows: rows.slice(start, start + ITEMS_PER_PAGE), page: safePage, totalPages };
};
// Wallet income that counts toward "Total Earned". Fund credits that carry a
// fundType go into the separate Funds balance, not the wallet, so they're left out.
const EARNING_TYPES = ['commission', 'bonus', 'leadership', 'royalty', 'reward', 'shop_cashback', 'fund_credit'];
const isCompleted = (t) => String(t.status || 'completed').toLowerCase() === 'completed';
const countsAsEarning = (t) => isCompleted(t) && EARNING_TYPES.includes(t.type) && !t.fundType && toNumber(t.amount) > 0;
// Money that left the wallet: payouts and admin deductions (stored negative),
// minus payouts that were reversed (refund rows with a PAYOUT_REVERSAL_ reference)
const withdrawnAmount = (t) => {
    if (!isCompleted(t)) return 0;
    if (t.type === 'withdrawal') return Math.abs(toNumber(t.amount));
    if (t.type === 'refund' && /^PAYOUT_REVERSAL_/.test(t.reference || '')) return -Math.abs(toNumber(t.amount));
    return 0;
};
const LOAD_SECTIONS = {
    summary: 'wallet summary',
    credited: 'credited amounts',
    payouts: 'payout history',
    coins: 'coin transactions',
    funds: 'funds',
    special: 'special income',
    recharges: 'recharges',
    topups: 'wallet top-ups',
};

const WalletTransactions = () => {
    const { user } = useAuthStore();
    const [tab, setTab] = useState('credited');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [walletData, setWalletData] = useState(null);
    const [payoutHistory, setPayoutHistory] = useState([]);
    const [walletTransactions, setWalletTransactions] = useState([]);
    const [coinTransactions, setCoinTransactions] = useState([]);
    const [coinBalance, setCoinBalance] = useState(null);
    const [coinPage, setCoinPage] = useState(1);
    const [coinTotalPages, setCoinTotalPages] = useState(1);
    const [userFunds, setUserFunds] = useState(null);
    const [specialIncome, setSpecialIncome] = useState(null);
    const [creditedPage, setCreditedPage] = useState(1);
    const [creditedTotalPages, setCreditedTotalPages] = useState(1);
    const [payoutPage, setPayoutPage] = useState(1);
    const [payoutTotalPages, setPayoutTotalPages] = useState(1);
    const [rechargeTransactions, setRechargeTransactions] = useState([]);
    const [rechargeWalletTransactions, setRechargeWalletTransactions] = useState([]);
    const [rechargePage, setRechargePage] = useState(1);
    const [allCreditedTransactions, setAllCreditedTransactions] = useState([]);
    const [allPayouts, setAllPayouts] = useState([]);
    const [loadErrors, setLoadErrors] = useState([]);
    const markLoadError = (section, failed) =>
        setLoadErrors((prev) => (failed ? [...new Set([...prev, section])] : prev.filter((s) => s !== section)));
    const [storyCard, setStoryCard] = useState(null);
    const closeStoryCard = useCallback(() => setStoryCard(null), []);

    const openTransactionCard = (t) => {
        const isPayout = t.type === 'payout_received';
        // type/amount/at let the server find old transactions saved without a fixed id
        const params = new URLSearchParams({ type: t.type, amount: String(t.amount) });
        const at = new Date(t.createdAt);
        if (t.createdAt && !Number.isNaN(at.getTime())) params.set('at', at.toISOString());
        const amount = formatShareAmount(t.amount);
        setStoryCard({
            url: `${API_ENDPOINTS.storyCard.transaction(t._id)}?${params}`,
            fileName: `YesITryMe-${isPayout ? 'Withdrawal' : 'Earning'}-${Math.round(Number(t.amount) || 0)}.jpg`,
            caption: isPayout
                ? `Got ₹${amount} in my bank from YesITryMe! ✅ Join me 👉 ${SITE_URL}`
                : `Earned ₹${amount} on YesITryMe! 🎉 Join me 👉 ${SITE_URL}`,
        });
    };

    const loadAll = () => {
        setError('');
        fetchWalletData();
        fetchPayoutHistory(1);
        fetchWalletTransactions(1);
        fetchCoinTransactions(1);
        fetchUserFunds();
        fetchSpecialIncome();
        fetchRechargeHistory();
        fetchRechargeWalletData();
    };

    // Reload only when the logged-in account changes, not on every user-object update
    useEffect(() => {
        if (user?.userId) loadAll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.userId]);

    const fetchWalletData = async () => {
        try {
            setLoading(true);
            // Fetch commission summary for wallet balance
            const response = await api.get(API_ENDPOINTS.packages.commissionSummary);
            if (response.data.success) {
                setWalletData(response.data.data);
                setError('');
            }
            markLoadError('summary', false);
        } catch (error) {
            console.error('Error fetching wallet data:', error);
            setError('Failed to fetch wallet data');
            markLoadError('summary', true);
        } finally {
            setLoading(false);
        }
    };

    // Credited tab: all rows are fetched once; pages are sliced here
    const showCreditedPage = (rows, page) => {
        const p = paginate(rows, page);
        setWalletTransactions(p.rows);
        setCreditedTotalPages(p.totalPages);
        setCreditedPage(p.page);
    };

    const fetchWalletTransactions = async (page = 1) => {
        try {
            // Fetch all commission transactions from the packages endpoint
            const response = await api.get(`${API_ENDPOINTS.packages.transactions}?limit=1000`);

            if (response.data.success) {
                const allTransactions = response.data.data?.transactions || [];
                // Filter out recharge transactions from credited tab
                const creditedTransactions = allTransactions.filter(t =>
                    t.type === 'commission' || t.type === 'bonus' || t.type === 'leadership' || t.type === 'royalty' || t.type === 'reward' || t.type === 'refund' || t.type === 'payout_received' || t.type === 'fund_credit' || t.type === 'withdrawal' || t.type === 'shop_cashback'
                );

                setAllCreditedTransactions(creditedTransactions);
                showCreditedPage(creditedTransactions, page);
            }
            markLoadError('credited', false);
        } catch (error) {
            console.error('Error fetching wallet transactions:', error);
            markLoadError('credited', true);
        }
    };

    const fetchRechargeWalletData = async () => {
        try {
            // The server returns at most 200 rows per request
            const response = await api.get(`${API_ENDPOINTS.recharge.walletTransactions}?limit=200`);
            if (response.data.success) {
                setRechargeWalletTransactions(response.data.transactions || []);
            }
            markLoadError('topups', false);
        } catch (error) {
            console.error('Error fetching recharge wallet transactions:', error);
            markLoadError('topups', true);
        }
    };

    const fetchCoinTransactions = async (page = 1) => {
        try {
            // Fetch coin balance and transactions
            const balanceResponse = await api.get(API_ENDPOINTS.coins.balance);
            if (balanceResponse.data.success) {
                setCoinBalance(balanceResponse.data.data);
            }

            // Fetch all coin transactions
            const transactionsResponse = await api.get(`${API_ENDPOINTS.coins.transactions}?limit=1000`);
            if (transactionsResponse.data.success) {
                const allTransactions = transactionsResponse.data.data?.transactions || [];
                // Include both earning and withdrawal transactions in pagination
                const allCoinTransactions = allTransactions.filter(t =>
                    t.type === 'view' ||
                    t.type === 'like' ||
                    t.type === 'comment' ||
                    t.type === 'share' ||
                    t.type === 'subscribe' || // legacy
                    t.type === 'activation_bonus' ||
                    t.type === 'referral_bonus' ||
                    t.type === 'recharge_bonus' ||
                    t.type === 'withdrawal' ||
                    t.type === 'product_order' ||
                    t.type === 'admin_adjust' // refunds of rejected withdrawals, admin corrections
                );

                // Sort by date (newest first)
                allCoinTransactions.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

                const p = paginate(allCoinTransactions, page);
                setCoinTransactions(p.rows);
                setCoinTotalPages(p.totalPages);
                setCoinPage(p.page);
            }
            markLoadError('coins', false);
        } catch (error) {
            console.error('Error fetching coin transactions:', error);
            markLoadError('coins', true);
        }
    };

    // Payout tab: all payouts are fetched once (cards need all of them); pages are sliced here
    const showPayoutPage = (payouts, page) => {
        const p = paginate(payouts, page);
        setPayoutHistory(p.rows);
        setPayoutTotalPages(p.totalPages);
        setPayoutPage(p.page);
    };

    const fetchPayoutHistory = async (page = 1) => {
        try {
            const response = await api.get(`${API_ENDPOINTS.payout.history}?limit=500`);
            if (response.data.success) {
                const payouts = response.data.payouts || [];
                setAllPayouts(payouts);
                showPayoutPage(payouts, page);
            }
            markLoadError('payouts', false);
        } catch (error) {
            console.error('Error fetching payout history:', error);
            markLoadError('payouts', true);
        }
    };

    const fetchUserFunds = async () => {
        try {
            const response = await api.get(`${API_ENDPOINTS.funds.userFunds}/${user.userId}`);

            const fundsData = response.data.data || {
                mobileFund: 0,
                laptopFund: 0,
                bikeFund: 0,
                carFund: 0,
                houseFund: 0,
                travelFund: 0,
                totalFunds: 0
            };

            setUserFunds(fundsData);
            markLoadError('funds', false);
        } catch (error) {
            console.error('Error fetching user funds:', error);
            // 404 just means no funds yet
            markLoadError('funds', error.response?.status !== 404);
            setUserFunds({
                mobileFund: 0,
                laptopFund: 0,
                bikeFund: 0,
                carFund: 0,
                houseFund: 0,
                travelFund: 0,
                totalFunds: 0
            });
        }
    };

    const fetchSpecialIncome = async () => {
        try {
            const response = await api.get(`${API_ENDPOINTS.specialIncome.user.replace(':userId', user.userId)}`);

            const specialIncomeData = response.data.data || {
                leaderShipFund: 0,
                royaltyIncome: 0,
                rewardIncome: 0
            };

            setSpecialIncome(specialIncomeData);
            markLoadError('special', false);
        } catch (error) {
            console.error('Error fetching special income:', error);
            // 404 just means no special income yet
            markLoadError('special', error.response?.status !== 404);
            setSpecialIncome({
                leaderShipFund: 0,
                royaltyIncome: 0,
                rewardIncome: 0
            });
        }
    };

    // Loads all recharges at once; the tab merges them with wallet top-ups and pages them here
    const fetchRechargeHistory = async () => {
        try {
            const response = await getRechargeHistory(1, 500, 'all');
            if (response.success) {
                const recharges = response.data?.recharges || [];
                const normalized = recharges.map((recharge) => {
                    const autoResolved =
                        recharge.rechargeCompletedAt && recharge.status === 'success';

                    if (
                        (recharge.status === 'processing' || recharge.status === 'payment_success') &&
                        recharge.rechargeCompletedAt &&
                        !recharge.failureReason
                    ) {
                        return {
                            ...recharge,
                            status: 'success',
                            autoResolved: true,
                        };
                    }

                    if (autoResolved && recharge.failureReason === 'wallet_confirmation') {
                        return {
                            ...recharge,
                            status: 'success',
                            autoResolved: true,
                        };
                    }

                    return recharge;
                });

                setRechargeTransactions(normalized);
            }
            markLoadError('recharges', false);
        } catch (error) {
            console.error('Error fetching recharge history:', error);
            setRechargeTransactions([]);
            markLoadError('recharges', true);
        }
    };

    // Get wallet top-up transactions separately
    const getWalletTopUps = () => {
        return rechargeWalletTransactions
            .filter(txn =>
                txn.type === 'topup' ||
                txn.type === 'admin_adjustment'
            )
            .map(txn => {
                const amount = parseFloat(txn.amount || 0);
                const isAdminAdjustment = txn.type === 'admin_adjustment';
                return {
                    _id: txn._id || `wallet_${txn.createdAt}`,
                    source: 'wallet',
                    transactionType: txn.type,
                    amount: Math.abs(amount),
                    rawAmount: amount,
                    netAmount: Math.abs(amount),
                    status: txn.status || 'completed',
                    createdAt: txn.createdAt,
                    date: txn.createdAt,
                    description: txn.description || (isAdminAdjustment ? 'Admin Smart Wallet Adjustment' : 'Wallet Top-Up'),
                    reference: txn.reference,
                    adminNotes: txn.adminNotes,
                    isTopUp: true,
                    adminAction: isAdminAdjustment,
                    adminActionType: amount >= 0 ? 'credit' : 'debit',
                    adminActionLabel: isAdminAdjustment
                        ? amount >= 0
                            ? 'Admin Added Funds'
                            : 'Admin Deducted Funds'
                        : null,
                };
            })
            .sort((a, b) => new Date(b.date) - new Date(a.date));
    };

    // Combine recharge history with wallet transactions
    const getAllRechargeTransactions = () => {
        // Map all recharge history transactions with all fields from server
        const historyTransactions = rechargeTransactions.map(recharge => ({
            ...recharge, // Include all fields from server (status, failureReason, aiTopUpMessage, etc.)
            source: 'history',
            transactionType: 'recharge',
            amount: parseFloat(recharge.amount || 0),
            netAmount: parseFloat(recharge.netAmount || recharge.amount || 0),
            date: recharge.rechargeCompletedAt || recharge.paymentCompletedAt || recharge.createdAt,
        }));

        // Create a map to match wallet transactions with recharge history
        const historyMap = new Map();
        historyTransactions.forEach(recharge => {
            const key = `${recharge.mobileNumber}_${recharge.amount}_${new Date(recharge.createdAt).toDateString()}`;
            if (!historyMap.has(key)) {
                historyMap.set(key, []);
            }
            historyMap.get(key).push(recharge);
        });

        const walletTxns = rechargeWalletTransactions
            .filter(txn => txn.type !== 'recharge_refund' && txn.type !== 'topup') // Exclude topup transactions from recharge display
            .map(txn => {
                // Transfers are handled separately
                if (txn.type === 'transfer_in' || txn.type === 'transfer_out') {
                    const amount = Math.abs(Number(txn.amount) || 0);
                    return {
                        _id: txn._id || `wallet_${txn.createdAt}`,
                        source: 'wallet',
                        transactionType: txn.type,
                        amount,
                        rawAmount: txn.type === 'transfer_out' ? -amount : amount,
                        netAmount: amount,
                        status: txn.status || 'completed',
                        createdAt: txn.createdAt,
                        date: txn.createdAt,
                        description: txn.description || '',
                        walletTransaction: true,
                        isTransfer: true,
                        transferDirection: txn.type === 'transfer_out' ? 'out' : 'in',
                        counterpartyName: txn.counterpartyName || '',
                        counterpartyMobile: txn.counterpartyMobile || '',
                        counterpartyUserId: txn.counterpartyUserId || '',
                        note: txn.note,
                        reference: txn.reference,
                    };
                }

                // Try to extract recharge details from description
                const description = txn.description || '';
                const mobileMatch = description.match(/\+91\s*(\d{10})|(\d{10})/);
                const operatorMatch = description.match(/(Airtel|Vodafone|JIO|Idea|BSNL|RELIANCE JIO)/i);
                const discountMatch = description.match(/(\d+(?:\.\d+)?)%\s*off/i);
                const discountAmountMatch = description.match(/-₹(\d+(?:\.\d+)?)\s+discount/);

                const mobileNumber = mobileMatch ? (mobileMatch[1] || mobileMatch[2]) : '';
                const amount = Math.abs(txn.amount || 0);
                const txnDate = new Date(txn.createdAt).toDateString();

                // Try to find matching recharge history entry
                let matchedRecharge = null;
                if (mobileNumber && amount) {
                    const key = `${mobileNumber}_${amount}_${txnDate}`;
                    const possibleMatches = historyMap.get(key) || [];
                    // Find the closest match by time (within 5 minutes)
                    matchedRecharge = possibleMatches.find(r => {
                        const timeDiff = Math.abs(new Date(r.createdAt) - new Date(txn.createdAt));
                        return timeDiff < 5 * 60 * 1000; // 5 minutes
                    });

                    // If no exact match, try to find by mobile and amount only (within 1 hour)
                    if (!matchedRecharge && possibleMatches.length > 0) {
                        matchedRecharge = possibleMatches.find(r => {
                            const timeDiff = Math.abs(new Date(r.createdAt) - new Date(txn.createdAt));
                            return timeDiff < 60 * 60 * 1000; // 1 hour
                        });
                    }
                }

                // For recharge_payment, use matched recharge details if available
                const baseTxn = {
                    _id: txn._id || `wallet_${txn.createdAt}`,
                    source: 'wallet',
                    transactionType: txn.type,
                    mobileNumber: mobileNumber || (matchedRecharge?.mobileNumber || 'N/A'),
                    operator: operatorMatch ? operatorMatch[1] : (matchedRecharge?.operator || 'N/A'),
                    amount: amount,
                    netAmount: amount,
                    discountPercentage: discountMatch ? parseFloat(discountMatch[1]) : (matchedRecharge?.discountPercentage || 0),
                    discountAmount: discountAmountMatch ? parseFloat(discountAmountMatch[1]) : (matchedRecharge?.discountAmount || 0),
                    status: txn.type === 'recharge_refund' ? 'refunded' : (matchedRecharge?.status || txn.status || 'success'),
                    rechargeType: matchedRecharge?.rechargeType || 'prepaid',
                    circle: matchedRecharge?.circle,
                    createdAt: txn.createdAt,
                    date: txn.createdAt,
                    description: description,
                    walletTransaction: true,
                };

                // If we found a matching recharge with failure details, include them
                if (matchedRecharge) {
                    baseTxn.failureReason = matchedRecharge.failureReason;
                    baseTxn.aiTopUpMessage = matchedRecharge.aiTopUpMessage;
                    baseTxn.aiTopUpErrorCode = matchedRecharge.aiTopUpErrorCode;
                    baseTxn.aiTopUpOrderId = matchedRecharge.aiTopUpOrderId;
                    baseTxn.aiTopUpTransactionId = matchedRecharge.aiTopUpTransactionId;
                    baseTxn.status = matchedRecharge.status; // Use status from recharge history
                }

                return baseTxn;
            });

        // Remove duplicates - if a wallet transaction matches a history transaction, prefer history
        const seen = new Set();
        const uniqueTransactions = [];

        // First add all history transactions
        historyTransactions.forEach(txn => {
            const date = new Date(txn.createdAt);
            const dateKey = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}-${date.getHours()}-${date.getMinutes()}`;
            const key = `${txn.mobileNumber}_${txn.amount}_${dateKey}`;
            if (!seen.has(key)) {
                seen.add(key);
                uniqueTransactions.push(txn);
            }
        });

        // Then add wallet transactions that don't have matches in history
        walletTxns.forEach(txn => {
            if (txn.isTransfer || txn.transactionType === 'transfer_in' || txn.transactionType === 'transfer_out') {
                const key = txn._id || `${txn.transactionType}_${txn.date}`;
                if (!seen.has(key)) {
                    seen.add(key);
                    uniqueTransactions.push(txn);
                }
                return;
            }

            // Skip transactions with invalid mobile numbers (like "N/A") - these are likely topups or other non-recharge transactions
            if (!txn.mobileNumber || txn.mobileNumber === 'N/A' || !/^\d{10}$/.test(txn.mobileNumber.replace(/\D/g, ''))) {
                return; // Skip this transaction
            }

            // Check if we already have this in history (within 2 minutes)
            // Check if there's a matching history entry by mobile, amount (original or net), and time
            const hasMatch = historyTransactions.some(ht => {
                if (ht.mobileNumber === txn.mobileNumber) {
                    // Match by either original amount or net amount (within 0.01 difference)
                    const amountMatch = Math.abs(ht.amount - txn.amount) < 0.01 ||
                        Math.abs((ht.netAmount || ht.amount) - txn.amount) < 0.01;
                    if (amountMatch) {
                        const timeDiff = Math.abs(new Date(ht.createdAt) - new Date(txn.createdAt));
                        return timeDiff < 2 * 60 * 1000; // 2 minutes
                    }
                }
                return false;
            });

            if (!hasMatch) {
                // Also check if we've already added a similar transaction
                const date = new Date(txn.createdAt);
                const dateKey = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}-${date.getHours()}-${date.getMinutes()}`;
                const key = `${txn.mobileNumber}_${txn.amount}_${dateKey}`;
                if (!seen.has(key)) {
                    seen.add(key);
                    uniqueTransactions.push(txn);
                }
            }
        });

        // Sort by date (newest first)
        uniqueTransactions.sort((a, b) => {
            const dateA = new Date(a.date || a.createdAt);
            const dateB = new Date(b.date || b.createdAt);
            return dateB - dateA;
        });
        return uniqueTransactions;
    };


    if (!user) {
        return <LoginPrompt type="walletTransactions" />
    }

    if (loading) {
        return (
            <div className="max-w-2xl mx-auto mt-10 bg-gradient-to-br from-blue-50 via-white to-green-50 rounded-3xl shadow-2xl border-2 border-blue-100/40 p-8 relative overflow-hidden">
                <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                </div>
            </div>
        );
    }

    const formatDate = (dateString) => {
        const d = new Date(dateString);
        return dateString && !Number.isNaN(d.getTime()) ? d.toLocaleDateString('en-IN') : '—';
    };

    const formatDateTime = (dateString) => {
        const d = new Date(dateString);
        return dateString && !Number.isNaN(d.getTime()) ? d.toLocaleString('en-IN') : '—';
    };

    const PaginationComponent = ({ currentPage, totalPages, onPageChange, colorClass = 'blue' }) => {
        if (totalPages <= 1) return null;

        const getColorClasses = (color) => {
            switch (color) {
                case 'blue':
                    return {
                        active: 'bg-blue-600 text-white',
                        inactive: 'bg-blue-100 text-blue-700 hover:bg-blue-200',
                        disabled: 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    };
                case 'yellow':
                    return {
                        active: 'bg-yellow-600 text-white',
                        inactive: 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200',
                        disabled: 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    };
                case 'purple':
                    return {
                        active: 'bg-purple-600 text-white',
                        inactive: 'bg-purple-100 text-purple-700 hover:bg-purple-200',
                        disabled: 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    };
                case 'green':
                    return {
                        active: 'bg-green-600 text-white',
                        inactive: 'bg-green-100 text-green-700 hover:bg-green-200',
                        disabled: 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    };
                default:
                    return {
                        active: 'bg-blue-600 text-white',
                        inactive: 'bg-blue-100 text-blue-700 hover:bg-blue-200',
                        disabled: 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    };
            }
        };

        const colors = getColorClasses(colorClass);

        return (
            <div className="mt-6 flex justify-center items-center gap-2">
                <button
                    onClick={() => onPageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${currentPage === 1 ? colors.disabled : colors.inactive
                        }`}
                >
                    Previous
                </button>

                <div className="flex items-center gap-1">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNum;
                        if (totalPages <= 5) {
                            pageNum = i + 1;
                        } else if (currentPage <= 3) {
                            pageNum = i + 1;
                        } else if (currentPage >= totalPages - 2) {
                            pageNum = totalPages - 4 + i;
                        } else {
                            pageNum = currentPage - 2 + i;
                        }

                        return (
                            <button
                                key={pageNum}
                                onClick={() => onPageChange(pageNum)}
                                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${currentPage === pageNum ? colors.active : colors.inactive
                                    }`}
                            >
                                {pageNum}
                            </button>
                        );
                    })}
                </div>

                <button
                    onClick={() => onPageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${currentPage === totalPages ? colors.disabled : colors.inactive
                        }`}
                >
                    Next
                </button>
            </div>
        );
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'completed': return 'bg-green-100 text-green-700';
            case 'approved': return 'bg-blue-100 text-blue-700';
            case 'pending': return 'bg-yellow-100 text-yellow-700';
            case 'rejected': return 'bg-red-100 text-red-700';
            case 'failed': return 'bg-red-100 text-red-700';
            case 'cancelled': return 'bg-gray-100 text-gray-700';
            case 'success': return 'bg-green-100 text-green-700';
            case 'refunded': return 'bg-orange-100 text-orange-700';
            case 'processing': return 'bg-yellow-100 text-yellow-700';
            case 'payment_success': return 'bg-blue-100 text-blue-700';
            default: return 'bg-gray-100 text-gray-700';
        }
    };


    const getTransactionTypeIcon = (type) => {
        switch (type) {
            case 'commission': return <ArrowUpCircle className="text-green-600" size={16} />;
            case 'withdrawal': return <ArrowDownCircle className="text-red-600" size={16} />;
            case 'refund': return <ArrowUpCircle className="text-blue-600" size={16} />;
            case 'shop_cashback': return <ShoppingCart className="text-emerald-600" size={16} />;
            case 'bonus': return <CheckCircle className="text-purple-600" size={16} />;
            case 'leadership': return <Crown className="text-purple-600" size={16} />;
            case 'royalty': return <Star className="text-orange-600" size={16} />;
            case 'reward': return <Gift className="text-pink-600" size={16} />;
            case 'payout_received': return <CheckCircle className="text-green-600" size={16} />;
            case 'fund_credit': return <Wallet className="text-blue-600" size={16} />;
            case 'special_income_credit': return <Star className="text-purple-600" size={16} />;
            case 'admin_adjust': return <Wallet className="text-green-600" size={16} />;
            case 'recharge_payment': return <ArrowDownCircle className="text-orange-600" size={16} />;
            case 'recharge_refund': return <ArrowUpCircle className="text-orange-600" size={16} />;
            case 'transfer_in': return <ArrowUpCircle className="text-emerald-600" size={16} />;
            case 'transfer_out': return <ArrowDownCircle className="text-rose-600" size={16} />;
            // Coin transaction types
            case 'view': return <ArrowUpCircle className="text-blue-600" size={16} />;
            case 'like': return <ArrowUpCircle className="text-pink-600" size={16} />;
            case 'comment': return <ArrowUpCircle className="text-orange-600" size={16} />;
            case 'subscribe': return <ArrowUpCircle className="text-red-600" size={16} />;
            case 'activation_bonus': return <Gift className="text-green-600" size={16} />;
            case 'referral_bonus': return <Gift className="text-indigo-600" size={16} />;
            case 'recharge_bonus': return <BatteryCharging className="text-amber-600" size={16} />;
            case 'product_order': return <ShoppingCart className="text-orange-600" size={16} />;
            default: return <Wallet className="text-gray-600" size={16} />;
        }
    };

    const getTransactionTypeColor = (type) => {
        switch (type) {
            case 'commission': return 'text-green-700';
            case 'withdrawal': return 'text-red-700';
            case 'refund': return 'text-blue-700';
            case 'shop_cashback': return 'text-emerald-700';
            case 'bonus': return 'text-purple-700';
            case 'payout_received': return 'text-green-700';
            case 'fund_credit': return 'text-blue-700';
            case 'special_income_credit': return 'text-purple-700';
            case 'admin_adjust': return 'text-green-700';
            case 'recharge_payment': return 'text-orange-700';
            case 'recharge_refund': return 'text-orange-700';
            case 'transfer_in': return 'text-emerald-700';
            case 'transfer_out': return 'text-rose-700';
            // Coin transaction types
            case 'view': return 'text-blue-700';
            case 'like': return 'text-pink-700';
            case 'comment': return 'text-orange-700';
            case 'subscribe': return 'text-red-700';
            case 'activation_bonus': return 'text-green-700';
            case 'referral_bonus': return 'text-indigo-700';
            case 'recharge_bonus': return 'text-amber-800';
            case 'product_order': return 'text-orange-800';
            default: return 'text-gray-700';
        }
    };

    const getTransactionTypeBg = (type) => {
        switch (type) {
            case 'commission': return 'bg-green-50 border-green-200';
            case 'withdrawal': return 'bg-red-50 border-red-200';
            case 'recharge_payment': return 'bg-orange-50 border-orange-200';
            case 'recharge_refund': return 'bg-orange-50 border-orange-200';
            case 'refund': return 'bg-blue-50 border-blue-200';
            case 'shop_cashback': return 'bg-emerald-50 border-emerald-200';
            case 'bonus': return 'bg-purple-50 border-purple-200';
            case 'payout_received': return 'bg-green-50 border-green-200';
            case 'fund_credit': return 'bg-blue-50 border-blue-200';
            case 'special_income_credit': return 'bg-purple-50 border-purple-200';
            case 'admin_adjust': return 'bg-green-50 border-green-200';
            case 'transfer_in': return 'bg-emerald-50 border-emerald-200';
            case 'transfer_out': return 'bg-rose-50 border-rose-200';
            // Coin transaction types
            case 'view': return 'bg-blue-50 border-blue-200';
            case 'like': return 'bg-pink-50 border-pink-200';
            case 'comment': return 'bg-orange-50 border-orange-200';
            case 'subscribe': return 'bg-red-50 border-red-200';
            case 'activation_bonus': return 'bg-green-50 border-green-200';
            case 'referral_bonus': return 'bg-indigo-50 border-indigo-200';
            case 'recharge_bonus': return 'bg-amber-50 border-amber-200';
            case 'product_order': return 'bg-orange-50 border-orange-200';
            default: return 'bg-gray-50 border-gray-200';
        }
    };

    const coinRowClass = (transaction) => {
        if (transaction.type === 'product_order') {
            const n = Number(transaction.amount);
            if (n < 0) return 'bg-red-50 border-red-200';
            if (n > 0) return 'bg-emerald-50 border-emerald-200';
        }
        return getTransactionTypeBg(transaction.type);
    };

    // Use the regular transactions (now includes admin transactions)
    const creditedTransactions = walletTransactions;
    const allCoinTransactions = coinTransactions; // This now includes both earnings and withdrawals

    // Create virtual transaction entries for funds and special income
    const virtualFundTransactions = [];
    const virtualSpecialIncomeTransactions = [];

    // Add fund transactions if user has funds
    if (userFunds) {
        if (userFunds.mobileFund > 0) {
            virtualFundTransactions.push({
                _id: `fund_mobile_${user.userId}`,
                type: 'fund_credit',
                amount: userFunds.mobileFund,
                description: `Mobile Fund credit added by admin`,
                status: 'completed',
                fundType: 'mobileFund',
                createdAt: userFunds.lastUpdated || userFunds.updatedAt || null, // balance, not a single credit
                packageName: 'Mobile Fund',
                purchaserName: 'Admin',
                level: 'Fund'
            });
        }
        if (userFunds.laptopFund > 0) {
            virtualFundTransactions.push({
                _id: `fund_laptop_${user.userId}`,
                type: 'fund_credit',
                amount: userFunds.laptopFund,
                description: `Laptop Fund credit added by admin`,
                status: 'completed',
                fundType: 'laptopFund',
                createdAt: userFunds.lastUpdated || userFunds.updatedAt || null, // balance, not a single credit
                packageName: 'Laptop Fund',
                purchaserName: 'Admin',
                level: 'Fund'
            });
        }
        if (userFunds.bikeFund > 0) {
            virtualFundTransactions.push({
                _id: `fund_bike_${user.userId}`,
                type: 'fund_credit',
                amount: userFunds.bikeFund,
                description: `Bike Fund credit added by admin`,
                status: 'completed',
                fundType: 'bikeFund',
                createdAt: userFunds.lastUpdated || userFunds.updatedAt || null, // balance, not a single credit
                packageName: 'Bike Fund',
                purchaserName: 'Admin',
                level: 'Fund'
            });
        }
        if (userFunds.carFund > 0) {
            virtualFundTransactions.push({
                _id: `fund_car_${user.userId}`,
                type: 'fund_credit',
                amount: userFunds.carFund,
                description: `Car Fund credit added by admin`,
                status: 'completed',
                fundType: 'carFund',
                createdAt: userFunds.lastUpdated || userFunds.updatedAt || null, // balance, not a single credit
                packageName: 'Car Fund',
                purchaserName: 'Admin',
                level: 'Fund'
            });
        }
        if (userFunds.houseFund > 0) {
            virtualFundTransactions.push({
                _id: `fund_house_${user.userId}`,
                type: 'fund_credit',
                amount: userFunds.houseFund,
                description: `House Fund credit added by admin`,
                status: 'completed',
                fundType: 'houseFund',
                createdAt: userFunds.lastUpdated || userFunds.updatedAt || null, // balance, not a single credit
                packageName: 'House Fund',
                purchaserName: 'Admin',
                level: 'Fund'
            });
        }
        if (userFunds.travelFund > 0) {
            virtualFundTransactions.push({
                _id: `fund_travel_${user.userId}`,
                type: 'fund_credit',
                amount: userFunds.travelFund,
                description: `Travel Fund credit added by admin`,
                status: 'completed',
                fundType: 'travelFund',
                createdAt: userFunds.lastUpdated || userFunds.updatedAt || null, // balance, not a single credit
                packageName: 'Travel Fund',
                purchaserName: 'Admin',
                level: 'Fund'
            });
        }
    }

    // Add special income transactions if user has special income
    if (specialIncome) {
        if (specialIncome.leaderShipFund > 0) {
            virtualSpecialIncomeTransactions.push({
                _id: `special_leadership_${user.userId}`,
                type: 'special_income_credit',
                amount: specialIncome.leaderShipFund,
                description: `Leadership Fund credit added by admin`,
                status: 'completed',
                incomeType: 'leaderShipFund',
                createdAt: specialIncome.updatedAt || null, // balance, not a single credit
                packageName: 'Leadership Fund',
                purchaserName: 'Admin',
                level: 'Special Income'
            });
        }
        if (specialIncome.royaltyIncome > 0) {
            virtualSpecialIncomeTransactions.push({
                _id: `special_royalty_${user.userId}`,
                type: 'special_income_credit',
                amount: specialIncome.royaltyIncome,
                description: `Royalty Income credit added by admin`,
                status: 'completed',
                incomeType: 'royaltyIncome',
                createdAt: specialIncome.updatedAt || null, // balance, not a single credit
                packageName: 'Royalty Income',
                purchaserName: 'Admin',
                level: 'Special Income'
            });
        }
        if (specialIncome.rewardIncome > 0) {
            virtualSpecialIncomeTransactions.push({
                _id: `special_reward_${user.userId}`,
                type: 'special_income_credit',
                amount: specialIncome.rewardIncome,
                description: `Reward Income credit added by admin`,
                status: 'completed',
                incomeType: 'rewardIncome',
                createdAt: specialIncome.updatedAt || null, // balance, not a single credit
                packageName: 'Reward Income',
                purchaserName: 'Admin',
                level: 'Special Income'
            });
        }
    }

    // Total Earned: wallet income only (not withdrawals, payouts received, refunds or Funds credits)
    const creditedRowsForTotals = allCreditedTransactions.length ? allCreditedTransactions : creditedTransactions;
    const totalEarned = creditedRowsForTotals
        .filter(countsAsEarning)
        .reduce((sum, t) => sum + toNumber(t.amount), 0);

    // Total Withdrawn: money that left the wallet, minus reversed payouts.
    // From wallet rows, so it covers every page and leaves out Funds / special-income payouts.
    const totalWithdrawn = Math.max(0, creditedRowsForTotals.reduce((sum, t) => sum + withdrawnAmount(t), 0));



    return (
        <div className="max-w-6xl mx-auto mt-20 mb-10 space-y-6 ">
            {/* Wallet Transactions */}
            <div className="bg-gradient-to-br from-blue-50 via-white to-green-50 rounded-3xl shadow-2xl border-2 border-blue-100/40 p-8 relative overflow-hidden animate-fade-in">
                {/* Animated Glow Border */}
                <div className="absolute inset-0 rounded-3xl pointer-events-none z-0 animate-pulse bg-gradient-to-tr from-blue-200/30 via-green-200/20 to-pink-200/30 blur-xl opacity-60" />

                <div className="flex flex-wrap items-center gap-2 mb-6 z-10 relative">
                    <button
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-semibold text-sm transition-all border ${tab === 'credited' ? 'bg-blue-600 text-white shadow border-blue-500' : 'bg-blue-100 text-blue-700 hover:bg-blue-200 border-transparent'}`}
                        onClick={() => setTab('credited')}
                    >
                        <ArrowUpCircle size={14} />
                        <span className="hidden sm:inline">Credited Amount</span>
                        <span className="sm:hidden">Credited</span>
                    </button>
                    <button
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-semibold text-sm transition-all border ${tab === 'recharge' ? 'bg-orange-600 text-white shadow border-orange-500' : 'bg-orange-100 text-orange-700 hover:bg-orange-200 border-transparent'}`}
                        onClick={() => setTab('recharge')}
                    >
                        <Smartphone size={14} />
                        <span className="hidden sm:inline">Mobile Recharge</span>
                        <span className="sm:hidden">Recharge</span>
                    </button>
                    <button
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-semibold text-sm transition-all border ${tab === 'funds' ? 'bg-purple-600 text-white shadow border-purple-500' : 'bg-purple-100 text-purple-700 hover:bg-purple-200 border-transparent'}`}
                        onClick={() => setTab('funds')}
                    >
                        <Wallet size={14} />
                        <span className="hidden sm:inline">Credited Funds</span>
                        <span className="sm:hidden">Funds</span>
                    </button>

                    <button
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-semibold text-sm transition-all border ${tab === 'payout' ? 'bg-green-600 text-white shadow border-green-500' : 'bg-green-100 text-green-700 hover:bg-green-200 border-transparent'}`}
                        onClick={() => setTab('payout')}
                    >
                        <ArrowDownCircle size={14} />
                        <span className="hidden sm:inline">Payout History</span>
                        <span className="sm:hidden">Payout</span>
                    </button>
                    <button
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-semibold text-sm transition-all border ${tab === 'coins' ? 'bg-yellow-600 text-white shadow border-yellow-500' : 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200 border-transparent'}`}
                        onClick={() => setTab('coins')}
                    >
                        <Star size={14} />
                        <span className="hidden sm:inline">Coin Transactions</span>
                        <span className="sm:hidden">Coins</span>
                    </button>
                </div>
                <div className="z-10 relative">
                    {loadErrors.length > 0 && (
                        <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                            <div className="flex items-center gap-2">
                                <AlertCircle size={18} />
                                <span>
                                    Couldn't load {loadErrors.map((s) => LOAD_SECTIONS[s] || s).join(', ')}. Amounts shown may be incomplete.
                                </span>
                            </div>
                            <button
                                type="button"
                                onClick={loadAll}
                                className="rounded-full bg-amber-600 px-3 py-1 text-xs font-semibold text-white hover:bg-amber-700"
                            >
                                Try again
                            </button>
                        </div>
                    )}
                    {tab === 'credited' ? (
                        <>
                            {/* Summary Cards */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                                <div className="bg-blue-50/80 border-l-4 border-blue-500 rounded-xl p-4 shadow">
                                    <div className="flex items-center gap-3">
                                        <ArrowUpCircle className="text-blue-500" size={32} />
                                        <div>
                                            <div className="text-xs text-blue-700 font-semibold flex items-center gap-1"><IndianRupee size={16} /> Available Balance</div>
                                            <div className="text-2xl font-extrabold text-blue-700">{inr(walletData?.balance)}</div>
                                        </div>
                                    </div>
                                </div>
                                <div className="bg-red-50/80 border-l-4 border-red-500 rounded-xl p-4 shadow">
                                    <div className="flex items-center gap-3">
                                        <ArrowDownCircle className="text-red-500" size={32} />
                                        <div>
                                            <div className="text-xs text-red-700 font-semibold flex items-center gap-1"><IndianRupee size={16} /> Total Withdrawn</div>
                                            <div className="text-2xl font-extrabold text-red-700">{inr(totalWithdrawn)}</div>
                                        </div>
                                    </div>
                                </div>
                                <div className="bg-green-50/80 border-l-4 border-green-500 rounded-xl p-4 shadow">
                                    <div className="flex items-center gap-3">
                                        <ArrowUpCircle className="text-green-500" size={32} />
                                        <div>
                                            <div className="text-xs text-green-700 font-semibold flex items-center gap-1"><IndianRupee size={16} /> Total Earned</div>
                                            <div className="text-2xl font-extrabold text-green-700">{inr(totalEarned)}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {error && (
                                <div className="mb-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded flex items-center gap-2">
                                    <AlertCircle size={20} />
                                    {error}
                                </div>
                            )}

                            <div className="bg-white/80 rounded-xl shadow border border-blue-100 p-4">
                                {creditedTransactions.length === 0 ? (
                                    <div className="text-center py-8">
                                        <ArrowUpCircle className="text-gray-400 mx-auto mb-4" size={48} />
                                        <p className="text-gray-500 text-lg">No credited amounts yet</p>
                                        <p className="text-gray-400 text-sm mt-2">Your commission earnings and bonuses will appear here</p>
                                    </div>
                                ) : (
                                    <div className="space-y-3">
                                        {creditedTransactions.map((transaction) => (
                                            <div key={transaction._id || transaction.id} className={`rounded-lg p-4 border ${getTransactionTypeBg(transaction.type)}`}>
                                                <div className="flex justify-between items-center">
                                                    <div className="flex items-center gap-3">
                                                        {getTransactionTypeIcon(transaction.type)}
                                                        <div>
                                                            <div className={`font-semibold text-lg ${getTransactionTypeColor(transaction.type)}`}>
                                                                {inr(transaction.amount)}
                                                            </div>
                                                            <div className="text-sm text-gray-600">{transaction.description}</div>
                                                            {transaction.packageName && (
                                                                <div className="text-xs text-gray-500">Package: {transaction.packageName}</div>
                                                            )}
                                                            {transaction.purchaserName && (
                                                                <div className="text-xs text-gray-500">From: {transaction.purchaserName}</div>
                                                            )}
                                                            {transaction.level ? (
                                                                <div className="text-xs text-gray-500">Level: {transaction.level}</div>
                                                            ) : null}
                                                            {transaction.type === 'commission' && (
                                                                <div className="text-xs text-blue-600 font-semibold">
                                                                    {transaction.level ? `💰 Commission from Level ${transaction.level}` : '💰 Commission'}
                                                                </div>
                                                            )}
                                                            {transaction.type === 'bonus' && (
                                                                <div className="text-xs text-purple-600 font-semibold">
                                                                    🎁 Bonus Payment
                                                                </div>
                                                            )}
                                                            {transaction.type === 'leadership' && (
                                                                <div className="text-xs text-purple-600 font-semibold">
                                                                    👑 Leadership Fund
                                                                </div>
                                                            )}
                                                            {transaction.type === 'royalty' && (
                                                                <div className="text-xs text-orange-600 font-semibold">
                                                                    ⭐ Royalty Income
                                                                </div>
                                                            )}
                                                            {transaction.type === 'reward' && (
                                                                <div className="text-xs text-pink-600 font-semibold">
                                                                    🎯 Reward Income
                                                                </div>
                                                            )}
                                                            {transaction.type === 'payout_received' && (
                                                                <div className="text-xs text-green-600 font-semibold">
                                                                    ✅ Payout Received from YesITryMe
                                                                </div>
                                                            )}
                                                            {transaction.type === 'fund_credit' && transaction.description?.includes('Admin added') && (
                                                                <div className="text-xs text-green-600 font-semibold">
                                                                    💰 Admin Added Money
                                                                </div>
                                                            )}
                                                            {(transaction.incomeType === 'active' || transaction.incomeType === 'passive') && (
                                                                <div className={`inline-block text-xs font-semibold px-2 py-1 rounded-full whitespace-nowrap ${transaction.incomeType === 'active'
                                                                    ? 'bg-green-100 text-green-800'
                                                                    : 'bg-blue-100 text-blue-800'
                                                                    }`}>
                                                                    {transaction.incomeType === 'active' ? 'Active Income' : 'Passive Income'}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="text-right">
                                                        <div className="text-sm text-gray-600">{formatDateTime(transaction.createdAt)}</div>
                                                        <div className={`inline-block text-xs font-semibold mt-1 px-2 py-1 rounded-full whitespace-nowrap ${getStatusColor(transaction.status)}`}>
                                                            {statusLabel(transaction.status)}
                                                        </div>
                                                        {canShareTransaction(transaction) && (
                                                            <div>
                                                                <ShareButton onClick={() => openTransactionCard(transaction)} />
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Pagination for Credited Amount */}
                            <PaginationComponent
                                currentPage={creditedPage}
                                totalPages={creditedTotalPages}
                                onPageChange={(page) => showCreditedPage(allCreditedTransactions, page)}
                                colorClass="blue"
                            />
                        </>
                    ) : tab === 'coins' ? (
                        <>
                            <div className="mb-4 flex flex-wrap items-center gap-2 rounded-xl border border-yellow-200 bg-yellow-50/90 px-4 py-3 text-sm text-yellow-900">
                                <IndianRupee className="w-4 h-4 shrink-0" />
                                <span>
                                    <strong>100 coins = ₹1</strong> — amounts below include the <strong>rupee equivalent</strong> (≈ ₹) for every coin total.
                                </span>
                            </div>
                            {/* Summary Cards for Coins */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                                <div className="bg-yellow-50/80 border-l-4 border-yellow-500 rounded-xl p-4 shadow">
                                    <div className="flex items-center gap-3">
                                        <Star className="text-yellow-500" size={32} />
                                        <div>
                                            <div className="text-xs text-yellow-700 font-semibold flex items-center gap-1">💰 Available Coins</div>
                                            <div className="text-2xl font-extrabold text-yellow-700">{coinBalance?.balance?.toLocaleString() || '0'}</div>
                                            <div className="text-sm font-semibold text-yellow-800/90 mt-0.5 flex items-center gap-1">
                                                <IndianRupee className="w-3.5 h-3.5" />
                                                ≈ {formatRupeesFromCoins(coinBalance?.balance)} value
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="bg-green-50/80 border-l-4 border-green-500 rounded-xl p-4 shadow">
                                    <div className="flex items-center gap-3">
                                        <ArrowUpCircle className="text-green-500" size={32} />
                                        <div>
                                            <div className="text-xs text-green-700 font-semibold flex items-center gap-1">📈 Total Earned</div>
                                            <div className="text-2xl font-extrabold text-green-700">{coinBalance?.totalEarned?.toLocaleString() || '0'}</div>
                                            <div className="text-sm font-semibold text-green-800/90 mt-0.5 flex items-center gap-1">
                                                <IndianRupee className="w-3.5 h-3.5" />
                                                ≈ {formatRupeesFromCoins(coinBalance?.totalEarned)} value
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="bg-blue-50/80 border-l-4 border-blue-500 rounded-xl p-4 shadow">
                                    <div className="flex items-center gap-3">
                                        <CheckCircle className="text-blue-500" size={32} />
                                        <div>
                                            <div className="text-xs text-blue-700 font-semibold flex items-center gap-1">💎 Active Income</div>
                                            <div className="text-2xl font-extrabold text-blue-700">{coinBalance?.activeIncome?.toLocaleString() || '0'}</div>
                                            <div className="text-sm font-semibold text-blue-800/90 mt-0.5 flex items-center gap-1">
                                                <IndianRupee className="w-3.5 h-3.5" />
                                                ≈ {formatRupeesFromCoins(coinBalance?.activeIncome)} value
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white/80 rounded-xl shadow border border-yellow-100 p-6">
                                {allCoinTransactions.length === 0 ? (
                                    <div className="text-center py-8">
                                        <Star className="text-gray-400 mx-auto mb-4" size={48} />
                                        <p className="text-gray-500 text-lg">No coin transactions yet</p>
                                        <p className="text-gray-400 text-sm mt-2">Your coin earnings, withdrawals, and shop deductions (refunds) will appear here</p>
                                    </div>
                                ) : (
                                    <div className="space-y-4">
                                        {allCoinTransactions.map((transaction) => (
                                            <div key={transaction._id || transaction.id} className={`rounded-lg p-5 border ${coinRowClass(transaction)}`}>
                                                <div className="flex justify-between items-center">
                                                    <div className="flex items-center gap-4">
                                                        {getTransactionTypeIcon(transaction.type)}
                                                        <div>
                                                            <div className={`font-semibold text-lg ${
                                                                transaction.type === 'product_order' && Number(transaction.amount) < 0
                                                                    ? 'text-red-700'
                                                                    : transaction.type === 'product_order' && Number(transaction.amount) > 0
                                                                        ? 'text-emerald-700'
                                                                        : getTransactionTypeColor(transaction.type)
                                                            }`}>
                                                                {transaction.type === 'product_order' ? (
                                                                    <>
                                                                        {Number(transaction.amount) < 0 ? '−' : '+'}
                                                                        {Math.abs(Number(transaction.amount)).toLocaleString()} coins
                                                                        <span className="text-sm font-normal text-gray-600 ml-2">
                                                                            {Number(transaction.amount) < 0 ? '(deducted)' : '(credited)'}
                                                                        </span>
                                                                    </>
                                                                ) : transaction.type === 'withdrawal'
                                                                    ? `${Math.abs(transaction.amount).toLocaleString()} coins`
                                                                    : `${toNumber(transaction.amount).toLocaleString('en-IN')} coins`
                                                                }
                                                            </div>
                                                            <div className="text-sm font-semibold text-gray-800 mt-0.5 flex items-center gap-1">
                                                                <IndianRupee className="w-3.5 h-3.5 text-gray-600" />
                                                                ≈{' '}
                                                                {(transaction.type === 'withdrawal' && Number(transaction.amount) < 0) || (transaction.type === 'product_order' && Number(transaction.amount) < 0) ? '-' : ''}
                                                                ₹{formatRupeesFromCoins(transaction.amount)}
                                                            </div>
                                                            <div className="text-sm text-gray-600 mt-1">
                                                                {transaction.type === 'product_order' && Number(transaction.amount) < 0 && '🛒 Product shop — coins used at checkout'}
                                                                {transaction.type === 'product_order' && Number(transaction.amount) > 0 && '↩️ Product shop — coins refunded to wallet'}
                                                                {transaction.type === 'view' && '📺 View Task'}
                                                                {transaction.type === 'like' && '👍 Like Task'}
                                                                {transaction.type === 'comment' && '💬 Comment Task'}
                                                                {transaction.type === 'share' && '🔁 Share Task'}
                                                                {transaction.type === 'subscribe' && '🔔 Subscribe Task'}
                                                                {transaction.type === 'activation_bonus' && '🎁 Activation Bonus'}
                                                                {transaction.type === 'recharge_bonus' && '⚡ Recharge reward (10%)'}
                                                                {transaction.type === 'withdrawal' && '💸 Withdrawal Request'}
                                                            </div>
                                                            {transaction.metadata?.taskTitle && (
                                                                <div className="text-xs text-gray-500 mt-1">Task: {transaction.metadata.taskTitle}</div>
                                                            )}
                                                            {transaction.metadata?.videoUrl && (
                                                                <div className="text-xs text-gray-500 mt-1">Video: {transaction.metadata.videoUrl}</div>
                                                            )}
                                                            {transaction.metadata?.amountInRupees && (
                                                                <div className="text-xs text-gray-500 mt-1">
                                                                    Amount: ₹{transaction.metadata.amountInRupees}
                                                                </div>
                                                            )}
                                                            {transaction.type === 'view' && (
                                                                <div className="text-xs text-blue-600 font-semibold mt-1">
                                                                    📺 View Task Completed
                                                                </div>
                                                            )}
                                                            {transaction.type === 'like' && (
                                                                <div className="text-xs text-pink-600 font-semibold mt-1">
                                                                    👍 Like Task Completed
                                                                </div>
                                                            )}
                                                            {transaction.type === 'comment' && (
                                                                <div className="text-xs text-orange-600 font-semibold mt-1">
                                                                    💬 Comment Task Completed
                                                                </div>
                                                            )}
                                                            {transaction.type === 'share' && (
                                                                <div className="text-xs text-violet-600 font-semibold mt-1">
                                                                    🔁 Share Task Completed
                                                                </div>
                                                            )}
                                                            {transaction.type === 'subscribe' && (
                                                                <div className="text-xs text-red-600 font-semibold mt-1">
                                                                    🔔 Subscribe Task Completed
                                                                </div>
                                                            )}
                                                            {transaction.type === 'activation_bonus' && (
                                                                <div className="text-xs text-green-600 font-semibold mt-1">
                                                                    🎁 Welcome Bonus
                                                                </div>
                                                            )}
                                                            {transaction.type === 'referral_bonus' && (
                                                                <div className="text-xs text-indigo-600 font-semibold mt-1">
                                                                    🎁 Referral Signup Bonus
                                                                </div>
                                                            )}
                                                            {transaction.type === 'referral_bonus' && transaction.metadata?.referredUserName && (
                                                                <div className="text-xs text-gray-500 mt-1">Referred: {transaction.metadata.referredUserName}</div>
                                                            )}
                                                            {transaction.type === 'recharge_bonus' && (
                                                                <>
                                                                    <div className="text-xs text-amber-800 font-semibold mt-1">
                                                                        Successful recharge bonus — 10% of recharge amount as coins (100 coins = ₹1)
                                                                    </div>
                                                                    {transaction.metadata?.rechargeAmountRupees != null && (
                                                                        <div className="text-xs text-gray-600 mt-1">
                                                                            Recharge: ₹{Number(transaction.metadata.rechargeAmountRupees).toLocaleString('en-IN')}
                                                                            {transaction.metadata?.bonusValueRupees != null && (
                                                                                <> · Bonus value: ₹{Number(transaction.metadata.bonusValueRupees).toLocaleString('en-IN')}</>
                                                                            )}
                                                                        </div>
                                                                    )}
                                                                    {transaction.metadata?.equivalentRupeesLabel && (
                                                                        <div className="text-xs text-gray-500 mt-1">{transaction.metadata.equivalentRupeesLabel}</div>
                                                                    )}
                                                                    {(transaction.metadata?.mobileNumber || transaction.metadata?.operator) && (
                                                                        <div className="text-xs text-gray-500 mt-1">
                                                                            {[transaction.metadata?.operator, transaction.metadata?.mobileNumber].filter(Boolean).join(' · ')}
                                                                        </div>
                                                                    )}
                                                                </>
                                                            )}
                                                            {transaction.type === 'withdrawal' && (
                                                                <div className="text-xs text-red-600 font-semibold mt-1">
                                                                    💸 Withdrawal Request
                                                                </div>
                                                            )}
                                                            {transaction.type === 'product_order' && transaction.metadata?.orderNumber && (
                                                                <div className="text-xs text-gray-600 mt-1 font-medium">
                                                                    Order: {transaction.metadata.orderNumber}
                                                                    {transaction.metadata?.reason && (
                                                                        <span className="text-gray-500"> · {String(transaction.metadata.reason).replace(/_/g, ' ')}</span>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="text-right">
                                                        <div className="text-sm text-gray-600">{formatDateTime(transaction.createdAt)}</div>
                                                        <div className={`inline-block text-xs font-semibold mt-2 px-2 py-1 rounded-full whitespace-nowrap ${getStatusColor(transaction.status || 'completed')}`}>
                                                            {(transaction.status || 'completed').charAt(0).toUpperCase() + (transaction.status || 'completed').slice(1)}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Pagination for Coin Transactions */}
                            <PaginationComponent
                                currentPage={coinPage}
                                totalPages={coinTotalPages}
                                onPageChange={fetchCoinTransactions}
                                colorClass="yellow"
                            />
                        </>
                    ) : tab === 'funds' ? (
                        <>
                            {/* Summary Cards for Funds */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                                <div className="bg-purple-50/80 border-l-4 border-purple-500 rounded-xl p-4 shadow">
                                    <div className="flex items-center gap-3">
                                        <Wallet className="text-purple-500" size={32} />
                                        <div>
                                            <div className="text-xs text-purple-700 font-semibold flex items-center gap-1"><IndianRupee size={16} /> Total Funds</div>
                                            <div className="text-2xl font-extrabold text-purple-700">{inr(userFunds?.totalFunds)}</div>
                                        </div>
                                    </div>
                                </div>

                                <div className="bg-blue-50/80 border-l-4 border-blue-500 rounded-xl p-4 shadow">
                                    <div className="flex items-center gap-3">
                                        <Star className="text-blue-500" size={32} />
                                        <div>
                                            <div className="text-xs text-blue-700 font-semibold flex items-center gap-1"><IndianRupee size={16} /> Special Income</div>
                                            <div className="text-2xl font-extrabold text-blue-700">{inr(toNumber(specialIncome?.leaderShipFund) + toNumber(specialIncome?.royaltyIncome) + toNumber(specialIncome?.rewardIncome))}</div>
                                        </div>
                                    </div>
                                </div>
                                <div className="bg-green-50/80 border-l-4 border-green-500 rounded-xl p-4 shadow">
                                    <div className="flex items-center gap-3">
                                        <ArrowUpCircle className="text-green-500" size={32} />
                                        <div>
                                            <div className="text-xs text-green-700 font-semibold flex items-center gap-1"><IndianRupee size={16} /> Total Available</div>
                                            <div className="text-2xl font-extrabold text-green-700">{inr(toNumber(userFunds?.totalFunds) + toNumber(specialIncome?.leaderShipFund) + toNumber(specialIncome?.royaltyIncome) + toNumber(specialIncome?.rewardIncome))}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white/80 rounded-xl shadow border border-purple-100 p-4">
                                {/* Funds Section */}
                                {userFunds?.totalFunds > 0 && (
                                    <div className="mb-6">
                                        <h3 className="text-lg font-semibold text-purple-800 mb-4 flex items-center gap-2">
                                            <Wallet className="text-purple-600" size={20} />
                                            💰 Your Funds
                                        </h3>
                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                            {userFunds.mobileFund > 0 && (
                                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                                                    <div className="flex items-center gap-3 mb-2">
                                                        <div className="text-2xl">📱</div>
                                                        <div className="text-sm text-blue-600 font-medium">Mobile Fund</div>
                                                    </div>
                                                    <div className="text-xl font-bold text-blue-700">{inr(userFunds?.mobileFund)}</div>
                                                </div>
                                            )}
                                            {userFunds.laptopFund > 0 && (
                                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                                                    <div className="flex items-center gap-3 mb-2">
                                                        <div className="text-2xl">💻</div>
                                                        <div className="text-sm text-blue-600 font-medium">Laptop Fund</div>
                                                    </div>
                                                    <div className="text-xl font-bold text-blue-700">{inr(userFunds?.laptopFund)}</div>
                                                </div>
                                            )}
                                            {userFunds.bikeFund > 0 && (
                                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                                                    <div className="flex items-center gap-3 mb-2">
                                                        <div className="text-2xl">🏍️</div>
                                                        <div className="text-sm text-blue-600 font-medium">Bike Fund</div>
                                                    </div>
                                                    <div className="text-xl font-bold text-blue-700">{inr(userFunds?.bikeFund)}</div>
                                                </div>
                                            )}
                                            {userFunds.carFund > 0 && (
                                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                                                    <div className="flex items-center gap-3 mb-2">
                                                        <div className="text-2xl">🚗</div>
                                                        <div className="text-sm text-blue-600 font-medium">Car Fund</div>
                                                    </div>
                                                    <div className="text-xl font-bold text-blue-700">{inr(userFunds?.carFund)}</div>
                                                </div>
                                            )}
                                            {userFunds.houseFund > 0 && (
                                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                                                    <div className="flex items-center gap-3 mb-2">
                                                        <div className="text-2xl">🏠</div>
                                                        <div className="text-sm text-blue-600 font-medium">House Fund</div>
                                                    </div>
                                                    <div className="text-xl font-bold text-blue-700">{inr(userFunds?.houseFund)}</div>
                                                </div>
                                            )}
                                            {userFunds.travelFund > 0 && (
                                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                                                    <div className="flex items-center gap-3 mb-2">
                                                        <div className="text-2xl">✈️</div>
                                                        <div className="text-sm text-blue-600 font-medium">Travel Fund</div>
                                                    </div>
                                                    <div className="text-xl font-bold text-blue-700">{inr(userFunds?.travelFund)}</div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* Special Income Section */}
                                {specialIncome && (specialIncome.leaderShipFund > 0 || specialIncome.royaltyIncome > 0 || specialIncome.rewardIncome > 0) && (
                                    <div>
                                        <h3 className="text-lg font-semibold text-purple-800 mb-4 flex items-center gap-2">
                                            <Star className="text-purple-600" size={20} />
                                            ⭐ Your Special Income
                                        </h3>
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                            {specialIncome.leaderShipFund > 0 && (
                                                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                                                    <div className="flex items-center gap-3 mb-2">
                                                        <div className="text-2xl">👑</div>
                                                        <div className="text-sm text-purple-600 font-medium">Leadership Fund</div>
                                                    </div>
                                                    <div className="text-xl font-bold text-purple-700">{inr(specialIncome?.leaderShipFund)}</div>
                                                </div>
                                            )}
                                            {specialIncome.royaltyIncome > 0 && (
                                                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                                                    <div className="flex items-center gap-3 mb-2">
                                                        <div className="text-2xl">⭐</div>
                                                        <div className="text-sm text-purple-600 font-medium">Royalty Income</div>
                                                    </div>
                                                    <div className="text-xl font-bold text-purple-700">{inr(specialIncome?.royaltyIncome)}</div>
                                                </div>
                                            )}
                                            {specialIncome.rewardIncome > 0 && (
                                                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                                                    <div className="flex items-center gap-3 mb-2">
                                                        <div className="text-2xl">🎯</div>
                                                        <div className="text-sm text-purple-600 font-medium">Reward Income</div>
                                                    </div>
                                                    <div className="text-xl font-bold text-purple-700">{inr(specialIncome?.rewardIncome)}</div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* No Funds Message */}
                                {(!userFunds || userFunds.totalFunds === 0) && (!specialIncome || (specialIncome.leaderShipFund === 0 && specialIncome.royaltyIncome === 0 && specialIncome.rewardIncome === 0)) && (
                                    <div className="text-center py-12">
                                        <Wallet className="text-gray-400 mx-auto mb-4" size={48} />
                                        <p className="text-gray-500 text-lg">No funds or special income yet</p>
                                        <p className="text-gray-400 text-sm mt-2">Your funds and special income will appear here when added by admin</p>
                                    </div>
                                )}
                                {/* Fund and Special Income Transactions */}
                                {(virtualFundTransactions.length > 0 || virtualSpecialIncomeTransactions.length > 0) && (
                                    <div className="mt-8">
                                        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                                            <BookOpen className="text-purple-600" size={20} />
                                            Fund & Special Income Transactions
                                        </h3>
                                        <div className="space-y-3">
                                            {[...virtualFundTransactions, ...virtualSpecialIncomeTransactions].map((transaction) => (
                                                <div key={transaction._id} className={`rounded-lg p-4 border ${getTransactionTypeBg(transaction.type)}`}>
                                                    <div className="flex justify-between items-center">
                                                        <div className="flex items-center gap-3">
                                                            {getTransactionTypeIcon(transaction.type)}
                                                            <div>
                                                                <div className={`font-semibold text-lg ${getTransactionTypeColor(transaction.type)}`}>
                                                                    {inr(transaction.amount)}
                                                                </div>
                                                                <div className="text-sm text-gray-600">{transaction.description}</div>
                                                                {transaction.packageName && (
                                                                    <div className="text-xs text-gray-500">Package: {transaction.packageName}</div>
                                                                )}
                                                                {transaction.purchaserName && (
                                                                    <div className="text-xs text-gray-500">From: {transaction.purchaserName}</div>
                                                                )}
                                                                {transaction.level ? (
                                                                    <div className="text-xs text-gray-500">Level: {transaction.level}</div>
                                                                ) : null}
                                                                {transaction.type === 'fund_credit' && (
                                                                    <div className="text-xs text-blue-600 font-semibold">
                                                                        💰 Fund Credit Added From YesITryMe
                                                                    </div>
                                                                )}
                                                                {transaction.type === 'special_income_credit' && (
                                                                    <div className="text-xs text-purple-600 font-semibold">
                                                                        ⭐ Special Income Credit Added From YesITryMe
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <div className="text-right">
                                                            <div className="text-sm text-gray-600">{formatDateTime(transaction.createdAt)}</div>
                                                            <div className={`inline-block text-xs font-semibold mt-1 px-2 py-1 rounded-full whitespace-nowrap ${getStatusColor(transaction.status)}`}>
                                                                {statusLabel(transaction.status)}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}


                            </div>
                        </>
                    ) : tab === 'recharge' ? (
                        <>
                            {/* Summary Cards for Recharge */}
                            {(() => {
                                const allRecharges = getAllRechargeTransactions();
                                const walletTopUps = getWalletTopUps();
                                const smartWalletTopUps = walletTopUps.filter(t => t.transactionType === 'topup');
                                const successful = allRecharges.filter(r => r.status === 'success');
                                const failed = allRecharges.filter(r => r.status === 'failed');
                                const totalSpent = successful.reduce(
                                    (sum, r) => sum + (parseFloat(r.netAmount || r.amount) || 0),
                                    0
                                );
                                return (
                                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4 mb-4 sm:mb-6">
                                        <div className="bg-gradient-to-br from-orange-50 to-orange-100 border border-orange-200 rounded-lg p-3 sm:p-4 shadow-sm">
                                            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
                                                <Smartphone className="text-orange-600 flex-shrink-0" size={24} />
                                                <div className="flex-1 min-w-0">
                                                    <div className="text-[10px] sm:text-xs text-orange-700 font-medium truncate">Recharges</div>
                                                    <div className="text-lg sm:text-xl font-bold text-orange-800">{allRecharges.filter(r => r.transactionType === 'recharge_payment' || r.source === 'history').length}</div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 rounded-lg p-3 sm:p-4 shadow-sm">
                                            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
                                                <Wallet className="text-blue-600 flex-shrink-0" size={24} />
                                                <div className="flex-1 min-w-0">
                                                    <div className="text-[10px] sm:text-xs text-blue-700 font-medium truncate">Top-Ups</div>
                                                    <div className="text-lg sm:text-xl font-bold text-blue-800">{smartWalletTopUps.length}</div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="bg-gradient-to-br from-green-50 to-green-100 border border-green-200 rounded-lg p-3 sm:p-4 shadow-sm">
                                            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
                                                <CheckCircle className="text-green-600 flex-shrink-0" size={24} />
                                                <div className="flex-1 min-w-0">
                                                    <div className="text-[10px] sm:text-xs text-green-700 font-medium truncate">Success</div>
                                                    <div className="text-lg sm:text-xl font-bold text-green-800">{successful.length}</div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="bg-gradient-to-br from-red-50 to-red-100 border border-red-200 rounded-lg p-3 sm:p-4 shadow-sm">
                                            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
                                                <AlertCircle className="text-red-600 flex-shrink-0" size={24} />
                                                <div className="flex-1 min-w-0">
                                                    <div className="text-[10px] sm:text-xs text-red-700 font-medium truncate">Failed</div>
                                                    <div className="text-lg sm:text-xl font-bold text-red-800">{failed.length}</div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 rounded-lg p-3 sm:p-4 shadow-sm">
                                            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
                                                <IndianRupee className="text-blue-600 flex-shrink-0" size={24} />
                                                <div className="flex-1 min-w-0">
                                                    <div className="text-[10px] sm:text-xs text-blue-700 font-medium truncate">Spent</div>
                                                    <div className="text-lg sm:text-xl font-bold text-blue-800">
                                                        ₹{totalSpent.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })()}
                            <div className="bg-white/95 backdrop-blur rounded-2xl shadow-sm border border-gray-200">
                                {(() => {
                                    const allRecharges = getAllRechargeTransactions();
                                    const walletTopUps = getWalletTopUps();
                                    const allTransactions = [...allRecharges, ...walletTopUps].sort((a, b) => {
                                        const dateA = new Date(a.date || a.createdAt);
                                        const dateB = new Date(b.date || b.createdAt);
                                        return dateB - dateA;
                                    });

                                    if (allTransactions.length === 0) {
                                        return (
                                            <div className="text-center py-10">
                                                <Smartphone className="text-gray-400 mx-auto mb-3" size={48} />
                                                <p className="text-gray-600 font-semibold text-lg">No mobile recharges or wallet top-ups yet</p>
                                                <p className="text-gray-400 text-sm mt-1">Your recharge history and wallet top-ups will appear here.</p>
                                            </div>
                                        );
                                    }
                                    // Apply pagination
                                    const { rows: paginatedTransactions } = paginate(allTransactions, rechargePage);
                                    return (
                                        <div className="overflow-x-auto">
                                            <table className="min-w-full text-sm">
                                                <thead className="bg-gray-50 text-gray-500 uppercase text-xs tracking-wide">
                                                    <tr>
                                                        <th className="px-4 py-3 text-left font-semibold">Amount</th>
                                                        <th className="px-4 py-3 text-left font-semibold">Type & Details</th>
                                                        <th className="px-4 py-3 text-left font-semibold">Cashback &amp; coins</th>
                                                        <th className="px-4 py-3 text-left font-semibold">Status</th>
                                                        <th className="px-4 py-3 text-left font-semibold">Date & Time</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-gray-100 text-gray-700">
                                                    {paginatedTransactions.map((transaction) => {
                                                        const date = new Date(transaction.date || transaction.rechargeCompletedAt || transaction.paymentCompletedAt || transaction.createdAt);
                                                        const rawAmount = typeof transaction.rawAmount === 'number'
                                                            ? transaction.rawAmount
                                                            : parseFloat(transaction.netAmount || transaction.amount || 0);
                                                        const amount = Math.abs(rawAmount).toFixed(2);
                                                        const cashback = transaction.discountAmount > 0 ? `₹${transaction.discountAmount.toFixed(2)}` : null;
                                                        const isTransfer = transaction.transactionType === 'transfer_in' || transaction.transactionType === 'transfer_out';
                                                        const isTransferOut = transaction.transactionType === 'transfer_out';
                                                        const isSmartWalletEvent = transaction.isTopUp
                                                            || transaction.transactionType === 'topup'
                                                            || transaction.transactionType === 'admin_adjustment'
                                                            || isTransfer
                                                            || transaction.isTransfer;
                                                        const isAdminAdjustment = transaction.transactionType === 'admin_adjustment';
                                                        return (
                                                            <tr key={transaction._id} className="hover:bg-gray-50 transition-colors">
                                                                <td className="px-4 py-4 align-top">
                                                                    <div className={`font-semibold text-base ${isAdminAdjustment
                                                                        ? (rawAmount >= 0 ? 'text-green-600' : 'text-red-600')
                                                                        : isTransfer
                                                                            ? (isTransferOut ? 'text-rose-600' : 'text-emerald-600')
                                                                            : 'text-gray-900'}`}>
                                                                        {isAdminAdjustment
                                                                            ? `${rawAmount >= 0 ? '+' : '-'}₹${amount}`
                                                                            : isTransfer
                                                                                ? `${isTransferOut ? '-' : '+'}₹${amount}`
                                                                                : `₹${amount}`}
                                                                    </div>
                                                                    {!isSmartWalletEvent && (
                                                                        <p className="text-xs text-gray-500 mt-0.5 capitalize">{transaction.rechargeType || 'prepaid'}</p>
                                                                    )}
                                                                </td>
                                                                <td className="px-4 py-4 align-top">
                                                                    {isSmartWalletEvent ? (
                                                                        transaction.transactionType === 'admin_adjustment' ? (
                                                                            <>
                                                                                <p className="font-medium text-gray-900 flex items-center gap-2">
                                                                                    <Wallet className={transaction.adminActionType === 'debit' ? 'text-rose-600' : 'text-purple-600'} size={16} />
                                                                                    {transaction.adminActionType === 'debit' ? 'Admin Smart Wallet Debit' : 'Admin Smart Wallet Credit'}
                                                                                </p>
                                                                                <p className="text-xs text-gray-500 mt-0.5">
                                                                                    {transaction.description}
                                                                                </p>
                                                                            </>
                                                                        ) : isTransfer ? (
                                                                            <>
                                                                                <p className="font-medium text-gray-900 flex items-center gap-2">
                                                                                    <Wallet className={isTransferOut ? 'text-rose-600' : 'text-emerald-600'} size={16} />
                                                                                    {isTransferOut ? 'Transfer to' : 'Transfer from'} {transaction.counterpartyMobile || transaction.counterpartyUserId || 'User'}
                                                                                </p>
                                                                                <p className="text-xs text-blue-600 mt-0.5 font-medium">
                                                                                    💸 Super Wallet Transfer
                                                                                </p>
                                                                                {transaction.note && (
                                                                                    <p className="text-[11px] text-gray-500 mt-1 truncate max-w-[220px]" title={transaction.note}>
                                                                                        Note: {transaction.note}
                                                                                    </p>
                                                                                )}
                                                                                {transaction.reference && (
                                                                                    <p className="text-[10px] text-gray-400 font-mono mt-1 truncate max-w-[220px]" title={transaction.reference}>
                                                                                        Ref: {transaction.reference}
                                                                                    </p>
                                                                                )}
                                                                            </>
                                                                        ) : (
                                                                            <>
                                                                                <p className="font-medium text-gray-900 flex items-center gap-2">
                                                                                    <Wallet className="text-blue-600" size={16} />
                                                                                    Wallet Top-Up
                                                                                </p>
                                                                                <p className="text-xs text-blue-600 mt-0.5 font-medium">
                                                                                    💰 Smart Wallet Credit
                                                                                </p>
                                                                                {transaction.reference && (
                                                                                    <p className="text-[10px] text-gray-400 font-mono mt-1 truncate max-w-[200px]" title={transaction.reference}>
                                                                                        Ref: {transaction.reference}
                                                                                    </p>
                                                                                )}
                                                                            </>
                                                                        )
                                                                    ) : (
                                                                        <>
                                                                            <p className="font-medium text-gray-900">+91 {transaction.mobileNumber}</p>
                                                                            <p className="text-xs text-gray-500 mt-0.5">
                                                                                {transaction.operator || 'Unknown'} {transaction.circle && transaction.circle !== 'NA' ? `• ${transaction.circle}` : ''}
                                                                            </p>
                                                                        </>
                                                                    )}
                                                                </td>
                                                                <td className="px-4 py-4 align-top">
                                                                    {isSmartWalletEvent ? (
                                                                        <span className="text-xs text-gray-400">N/A</span>
                                                                    ) : (
                                                                        <div className="space-y-2">
                                                                            {cashback ? (
                                                                                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 px-2 py-1 text-xs font-semibold">
                                                                                    {transaction.discountPercentage}% • {cashback}
                                                                                </span>
                                                                            ) : (
                                                                                <span className="text-xs text-gray-400">No cashback</span>
                                                                            )}
                                                                            {transaction.status === 'success' && (() => {
                                                                                const face = parseFloat(transaction.amount || 0);
                                                                                if (face <= 0) return null;
                                                                                const coinsFromServer = parseInt(transaction.rechargeBonusCoins, 10);
                                                                                const coins = Number.isFinite(coinsFromServer) && coinsFromServer > 0
                                                                                    ? coinsFromServer
                                                                                    : Math.round(face * 10);
                                                                                const bonusRupees = Math.round(face * 0.1 * 100) / 100;
                                                                                return (
                                                                                    <div className="flex items-start gap-1.5 rounded-lg border border-amber-100 bg-amber-50/90 px-2 py-1.5 text-xs text-amber-900">
                                                                                        <Coins className="w-3.5 h-3.5 text-amber-600 shrink-0 mt-0.5" />
                                                                                        <div>
                                                                                            <span className="font-bold">+{coins.toLocaleString('en-IN')} coins</span>
                                                                                            <span className="flex items-center gap-1 text-xs font-semibold text-amber-900 mt-0.5">
                                                                                                <IndianRupee className="w-3 h-3" />
                                                                                                ≈ ₹{formatRupeesFromCoins(coins)} equivalent
                                                                                            </span>
                                                                                            <span className="block text-[11px] text-amber-800/95 mt-0.5">
                                                                                                Recharge bonus: 10% of ₹{face.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} (₹{bonusRupees.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} value; 100 coins = ₹1). Full line item under <strong>Coins</strong> tab.
                                                                                            </span>
                                                                                        </div>
                                                                                    </div>
                                                                                );
                                                                            })()}
                                                                        </div>
                                                                    )}
                                                                </td>
                                                                <td className="px-4 py-4 align-top">
                                                                    <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(transaction.status)}`}>
                                                                        {transaction.status.replace('_', ' ')}
                                                                    </span>
                                                                    {transaction.status === 'failed' && (transaction.failureReason || transaction.aiTopUpMessage) && (
                                                                        <p className="text-[11px] text-red-600 mt-1">
                                                                            {transaction.failureReason || transaction.aiTopUpMessage}
                                                                        </p>
                                                                    )}
                                                                </td>
                                                                <td className="px-4 py-4 align-top">
                                                                    <p className="text-sm font-medium text-gray-900">
                                                                        {date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                                                                    </p>
                                                                    <p className="text-xs text-gray-500">
                                                                        {date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                                                                    </p>
                                                                    {!isSmartWalletEvent && transaction.aiTopUpOrderId && (
                                                                        <p className="mt-1 text-[10px] text-gray-400 font-mono truncate max-w-[140px]" title={transaction.aiTopUpOrderId}>
                                                                            {transaction.aiTopUpOrderId}
                                                                        </p>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    );
                                })()}
                            </div>

                            {/* Pagination for Recharge History */}
                            {(() => {
                                const allRecharges = getAllRechargeTransactions();
                                const walletTopUps = getWalletTopUps();
                                const allTransactions = [...allRecharges, ...walletTopUps];
                                const totalPages = Math.ceil(allTransactions.length / 10);
                                return (
                                    <PaginationComponent
                                        currentPage={rechargePage}
                                        totalPages={totalPages || 1}
                                        onPageChange={(page) => setRechargePage(Math.min(Math.max(1, page), totalPages || 1))}
                                        colorClass="orange"
                                    />
                                );
                            })()}
                        </>
                    ) : (
                        <>
                            {/* Summary Cards */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                                <div className="bg-green-50/80 border-l-4 border-green-500 rounded-xl p-4 shadow">
                                    <div className="flex items-center gap-3">
                                        <ArrowDownCircle className="text-green-500" size={32} />
                                        <div>
                                            <div className="text-xs text-green-700 font-semibold flex items-center gap-1"><IndianRupee size={16} /> Total Requests</div>
                                            <div className="text-2xl font-extrabold text-green-700">{allPayouts.filter(p => p.status !== 'rejected').length}</div>
                                        </div>
                                    </div>
                                </div>

                                <div className="bg-blue-50/80 border-l-4 border-blue-500 rounded-xl p-4 shadow">
                                    <div className="flex items-center gap-3">
                                        <ArrowUpCircle className="text-blue-500" size={32} />
                                        <div>
                                            <div className="text-xs text-blue-700 font-semibold flex items-center gap-1"><IndianRupee size={16} /> Total Requested</div>
                                            <div className="text-2xl font-extrabold text-blue-700">{inr(allPayouts.filter(p => p.status !== 'rejected').reduce((sum, payout) => sum + toNumber(payout.amount), 0))}</div>
                                        </div>
                                    </div>
                                </div>

                                <div className="bg-purple-50/80 border-l-4 border-purple-500 rounded-xl p-4 shadow">
                                    <div className="flex items-center gap-3">
                                        <CheckCircle className="text-purple-500" size={32} />
                                        <div>
                                            <div className="text-xs text-purple-700 font-semibold flex items-center gap-1"><IndianRupee size={16} /> Net Received</div>
                                            <div className="text-2xl font-extrabold text-purple-700 ">{inr(allPayouts.filter(p => p.status === 'approved' || p.status === 'completed').reduce((sum, payout) => sum + toNumber(payout.netAmount ?? payout.amount), 0))}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white/80 rounded-xl shadow border border-green-100 p-4">
                                {payoutHistory.length === 0 ? (
                                    <div className="text-center py-8">
                                        <ArrowDownCircle className="text-gray-400 mx-auto mb-4" size={48} />
                                        <p className="text-gray-500 text-lg">No payout requests yet</p>
                                        <p className="text-gray-400 text-sm mt-2">Your withdrawal requests will appear here</p>
                                    </div>
                                ) : (
                                    <div className="space-y-3">
                                        {payoutHistory.map((payout) => (
                                            <div key={payout._id} className={`rounded-lg p-4 border ${getTransactionTypeBg('withdrawal')}`}>
                                                <div className="flex justify-between items-center">
                                                    <div className="flex items-center gap-3">
                                                        {getTransactionTypeIcon('withdrawal')}
                                                        <div>
                                                            <div className={`font-semibold text-lg ${getTransactionTypeColor('withdrawal')}`}>
                                                                {inr(payout.amount)}
                                                            </div>
                                                            <div className="text-sm text-gray-600">Withdrawal Request</div>
                                                            {/* Status-specific helper line */}
                                                            <div className={`text-xs font-semibold mt-1 ${payout.status === 'rejected'
                                                                ? 'text-red-600'
                                                                : payout.status === 'pending'
                                                                    ? 'text-yellow-600'
                                                                    : payout.status === 'approved'
                                                                        ? 'text-blue-600'
                                                                        : 'text-green-600'
                                                                }`}>
                                                                {payout.status === 'rejected' && '❌ Rejected by Admin'}
                                                                {payout.status === 'pending' && '⏳ Pending Review'}
                                                                {payout.status === 'approved' && '✅ Approved - Processing'}
                                                                {payout.status === 'completed' && '✅ Completed - Paid'}
                                                            </div>
                                                            <div className="text-xs text-gray-500">Requested on {formatDate(payout.requestDate)}</div>
                                                            {payout.paymentMethod && (
                                                                <div className="text-xs text-gray-500">Method: {payout.paymentMethod}</div>
                                                            )}
                                                            {(payout.paymentDetails?.accountNumber || payout.accountNumber) && (
                                                                <div className="text-xs text-gray-500">Account: {maskAccount(payout.paymentDetails?.accountNumber || payout.accountNumber)}</div>
                                                            )}
                                                            {(payout.paymentDetails?.ifscCode || payout.ifscCode) && (
                                                                <div className="text-xs text-gray-500">IFSC: {payout.paymentDetails?.ifscCode || payout.ifscCode}</div>
                                                            )}
                                                            {(payout.paymentDetails?.upiId || payout.upiId) && (
                                                                <div className="text-xs text-gray-500">UPI: {payout.paymentDetails?.upiId || payout.upiId}</div>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="text-right">
                                                        <div className="text-sm text-gray-600">{formatDateTime(payout.requestDate)}</div>
                                                        <div className={`inline-block text-xs font-semibold mt-1 px-2 py-1 rounded-full whitespace-nowrap ${getStatusColor(payout.status)}`}>
                                                            {statusLabel(payout.status)}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Pagination for Payout History */}
                            <PaginationComponent
                                currentPage={payoutPage}
                                totalPages={payoutTotalPages}
                                onPageChange={(page) => showPayoutPage(allPayouts, page)}
                                colorClass="green"
                            />
                        </>
                    )}
                </div>
                {/* Glassmorphism effect */}
                <div className="absolute inset-0 pointer-events-none rounded-3xl bg-gradient-to-br from-blue-100/30 via-white/10 to-green-100/30 blur-xl opacity-60 z-0" />
            </div>
            <StoryCardModal request={storyCard} onClose={closeStoryCard} />
        </div>
    );
};

export default WalletTransactions;